import {useState, useEffect, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Grid, Box, Typography} from '@mui/material';

import QuestionNavigator from '../../../../components/Navigator/QuestionNavigator';
import {useQuestionnaireNavigation} from '../../../../hooks/useQuestionnaireNavigation';
import {greenColors} from '../../../../layouts/theme/designSystem';
import {SetOfQuestions} from '../../../company/culture/surveys/components/survey-questionnaire/cultural-categories/SetOfQuestions';
import {Item} from '../../../company/culture/surveys/types/SurveyQuestionnaireTypes';
import {QuestionType, TalentSurvey} from '../../types';

type Props = {
  talentSurvey: TalentSurvey;
  onSubmit: (talentSurvey: TalentSurvey) => void;
  isScreeningSurvey?: boolean;
};

// Only difference is between Item and QuestionType is the resourceId is named "id". This transformation simplifies
// shared logic.
const transformIdToResourceId = (q: QuestionType): Item => ({
  resourceId: q.id,
  required: q.required,
  title: q.title,
  scaleConfiguration: q.configuration,
});

export default function Questionnaire({talentSurvey, onSubmit, isScreeningSurvey = false}: Props) {
  const {t} = useTranslation();
  const titleTranslationKey = isScreeningSurvey ? 'talents.survey.screeningApplicants.title' : 'talents.survey.title';
  const subtitleTranslationKey = isScreeningSurvey
    ? 'talents.survey.screeningApplicants.subtitle'
    : 'talents.survey.subtitle';

  const [startedDate, setStartedDate] = useState<string>('');

  const items: Item[][] = useMemo(
    () => talentSurvey.questions.map((question) => [transformIdToResourceId(question)]),
    [talentSurvey.questions]
  );
  const {onSectionIndexChange, sections, questionSections, answerSections, onAnswer, sectionIndex} =
    useQuestionnaireNavigation(items);

  useEffect(() => {
    // Is this logic necessary? Keeping it for now to preserve functionality. But I think this value should be set on
    // BE perhaps?
    if (!talentSurvey.startedAt && sectionIndex === 1 && !startedDate) {
      setStartedDate(new Date().toISOString());
    }
  }, [talentSurvey, startedDate, sectionIndex]);

  const onComplete = () => {
    const answers = answerSections.flat().map((a) => ({questionId: a.resourceId, value: a.value}));
    const updatedSurvey = {
      ...talentSurvey,
      startedDate: talentSurvey.startedAt || startedDate,
      answers,
    };

    onSubmit(updatedSurvey);
  };

  return (
    <>
      <Box textAlign='center' mb={{xs: 1, sm: 2}}>
        <Typography
          variant='h1'
          color={greenColors[700]}
          lineHeight={1}
          sx={{
            fontWeight: 700,
          }}
        >
          {t(titleTranslationKey)}
        </Typography>
      </Box>
      <Box textAlign='center'>
        <Typography
          variant='h2'
          color={greenColors[700]}
          lineHeight={1}
          sx={{
            fontWeight: 600,
          }}
        >
          {t(subtitleTranslationKey)}
        </Typography>
      </Box>
      <Box>
        <Grid container columns={12}>
          <Grid item xs={12}>
            <SetOfQuestions
              questions={questionSections[sectionIndex] || []}
              answers={answerSections[sectionIndex]}
              onAnswer={onAnswer}
            />
          </Grid>

          <Grid item xs={12}>
            <QuestionNavigator
              sections={sections}
              currentPosition={sectionIndex}
              onIndexChange={onSectionIndexChange}
              onComplete={onComplete}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
